/**
 * Utility functions for sanitizing input data before API calls
 * Only handles serialNo (trim) and email (trim + lowercase) fields
 */

/**
 * Sanitizes a string input by trimming whitespace
 * @param value - The input value to sanitize
 * @returns The trimmed string, or empty string if null/undefined
 */
export const sanitizeString = (value: string | null | undefined): string => {
  if (value === null || value === undefined) {
    return '';
  }
  return String(value).trim();
};

/**
 * Sanitizes an email input by trimming whitespace and converting to lowercase
 * @param email - The email value to sanitize
 * @returns The sanitized email (trimmed and lowercase), or empty string if null/undefined
 */
export const sanitizeEmail = (email: string | null | undefined): string => {
  if (email === null || email === undefined) {
    return '';
  }
  return String(email).trim().toLowerCase();
};

/**
 * Sanitizes an object by applying trim to serialNo and trim+lowercase to email properties
 * @param obj - The object to sanitize
 * @returns A new object with sanitized values
 */
export const sanitizeObject = <T extends Record<string, any>>(obj: T): T => {
  const sanitized = { ...obj } as T;

  for (const [key, value] of Object.entries(sanitized)) {
    if (typeof value === 'string') {
      // Apply lowercase for email fields
      if (key.toLowerCase() === 'email') {
        (sanitized as any)[key] = sanitizeEmail(value);
      }
      // Apply trim for serialNo fields
      else if (key.toLowerCase() === 'serialno') {
        (sanitized as any)[key] = sanitizeString(value);
      }
    }
  }

  return sanitized;
};

/**
 * Sanitizes variables object for GraphQL queries/mutations
 * Only processes serialNo and email fields
 * @param variables - The variables object to sanitize
 * @returns A new object with sanitized values
 */
export const sanitizeVariables = (variables: Record<string, any>): Record<string, any> => {
  return sanitizeObject(variables);
};